import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import {
  disconnectGoogleDrive,
  getGoogleDriveAuthUrl,
  getGoogleDriveStatus,
} from '../../../api/googleDrive';

function GoogleDriveIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function StatusDot({ connected, loading }) {
  if (loading) {
    return (
      <span
        className="h-2.5 w-2.5 rounded-full bg-slate-300 animate-pulse shrink-0"
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className={`h-2.5 w-2.5 rounded-full shrink-0 ${
        connected ? 'bg-emerald-500' : 'bg-amber-400'
      }`}
      aria-hidden="true"
    />
  );
}

function GoogleDriveConnectPanel({
  oauthConfigured = true,
  instructions = '',
  compact = false,
  onStatusChange,
  refreshKey = '',
}) {
  const [driveStatus, setDriveStatus] = useState({ connected: false, google_email: null });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const status = await getGoogleDriveStatus();
      setDriveStatus(status);
      onStatusChange?.(status);
    } catch {
      const status = { connected: false, google_email: null };
      setDriveStatus(status);
      onStatusChange?.(status);
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, refreshKey]);

  const handleConnect = async () => {
    setActionError('');
    setActionLoading(true);
    try {
      const { url } = await getGoogleDriveAuthUrl();
      window.location.href = url;
    } catch {
      setActionError('Não foi possível iniciar a conexão com o Google Drive.');
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setActionError('');
    setActionLoading(true);
    try {
      await disconnectGoogleDrive();
      const status = { connected: false, google_email: null };
      setDriveStatus(status);
      onStatusChange?.(status);
      setModalOpen(false);
    } catch {
      setActionError('Não foi possível desconectar o Google Drive.');
    } finally {
      setActionLoading(false);
    }
  };

  const connected = driveStatus.connected;
  const email = driveStatus.google_email;

  const statusLabel = loading
    ? 'Verificando conexão...'
    : connected
      ? 'Conta conectada'
      : 'Conta não conectada';

  const cardClass = compact
    ? 'rounded-xl border bg-white overflow-hidden'
    : 'surface-card shadow-card-md overflow-hidden';

  const borderClass = loading
    ? 'border-surface-border'
    : connected
      ? 'border-emerald-200'
      : 'border-amber-200';

  return (
    <>
      <div className={`${cardClass} ${borderClass}`}>
        <div
          className={`flex items-center gap-3 px-4 py-3.5 ${
            connected ? 'bg-emerald-50/80' : loading ? 'bg-slate-50' : 'bg-amber-50/60'
          }`}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white border border-surface-border shadow-sm">
            <GoogleDriveIcon />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <StatusDot connected={connected} loading={loading} />
              <p className="text-sm font-medium text-content-primary">{statusLabel}</p>
            </div>
            {!loading && connected && email && (
              <p className="text-xs text-content-secondary mt-0.5 truncate">{email}</p>
            )}
            {!loading && !connected && oauthConfigured && (
              <p className="text-xs text-content-muted mt-0.5">
                Conecte sua conta Google para importar pastas do Drive.
              </p>
            )}
          </div>

          {!loading && oauthConfigured && (
            <button
              type="button"
              onClick={() => {
                setActionError('');
                setModalOpen(true);
              }}
              className={
                connected
                  ? 'text-xs font-medium text-forge-700 hover:text-forge-800 shrink-0'
                  : 'btn-primary text-xs px-3 py-2 shrink-0'
              }
            >
              {connected ? 'Gerenciar' : 'Conectar'}
            </button>
          )}
        </div>

        {!loading && !oauthConfigured && (
          <div className="px-4 py-3 border-t border-amber-100 bg-amber-50/50">
            <p className="text-xs text-amber-900 font-medium">OAuth não configurado no servidor</p>
            <p className="text-xs text-amber-800 mt-1">
              {instructions ||
                'Peça ao administrador para definir GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET e GOOGLE_OAUTH_REDIRECT_URI.'}
            </p>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => !actionLoading && setModalOpen(false)}
        title={connected ? 'Conta Google conectada' : 'Conectar Google Drive'}
        disableClose={actionLoading}
      >
        <div className="space-y-5">
          {connected ? (
            <>
              <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-emerald-200">
                  <GoogleDriveIcon className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-emerald-900">Pronto para importar</p>
                  <p className="text-sm text-emerald-800 mt-1 break-all">{email}</p>
                  <p className="text-xs text-emerald-700/80 mt-2">
                    Esta conta será usada para ler pastas que você informar no cadastro da base.
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  disabled={actionLoading}
                  className="btn-secondary text-sm px-4 py-2.5 disabled:opacity-50"
                >
                  Fechar
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={actionLoading}
                  className="text-sm px-4 py-2.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {actionLoading ? 'Desconectando...' : 'Desconectar conta'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-content-secondary -mt-1">
                Autorize acesso <strong>somente leitura</strong> às suas pastas. Não alteramos nem
                publicamos arquivos.
              </p>

              <ol className="space-y-2 text-sm text-content-secondary">
                <li className="flex gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forge-100 text-forge-700 text-xs font-semibold">
                    1
                  </span>
                  <span>Clique em continuar com Google e faça login na conta certa.</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forge-100 text-forge-700 text-xs font-semibold">
                    2
                  </span>
                  <span>Volte aqui e cole o link da pasta que deseja importar.</span>
                </li>
              </ol>

              <button
                type="button"
                onClick={handleConnect}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-50"
              >
                <GoogleDriveIcon className="h-5 w-5" />
                {actionLoading ? 'Redirecionando...' : 'Continuar com Google'}
              </button>

              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={actionLoading}
                className="btn-secondary w-full text-sm py-2.5 disabled:opacity-50"
              >
                Cancelar
              </button>
            </>
          )}

          {actionError && (
            <p className="text-sm text-red-600" role="alert">
              {actionError}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}

export default GoogleDriveConnectPanel;
